import { useEffect, useState } from 'react';

import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import Grid from '@mui/material/Grid2';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import styled from '@mui/material/styles/styled';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import PropTypes from 'prop-types';
import { Controller, useFormContext, useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  CRAWLER_TYPES,
  JOB_TYPE,
} from '../../../../constants/crawlerConstants';
import Text from '../../../elements/text/Text';
import ProjectSetupIncomplete from '../../../sections/project-setup-incomplete/ProjectSetupIncomplete';
import {
  displayJobType,
  priceDisplay,
  projectSetupComplete,
  scheduleDisplay,
  seedsDisplay,
} from '../crawlerUtils';

const StyledTable = styled(Table)({
  '& tr > td, tr > th': {
    textAlign: 'center',
  },
});

const CrawlStep1 = ({ getFreeJobs, payment }) => {
  const { t } = useTranslation();
  const { control, watch, setValue } = useFormContext();
  const { errors } = useFormState({ control });

  const [freeJobs, setFreeJobs] = useState(null);

  const handleGetFreeJobs = async () => {
    const freeJobsRes = await getFreeJobs();
    setFreeJobs(freeJobsRes);
  };

  useEffect(() => {
    handleGetFreeJobs();
  }, []);

  const hasFreeJobs = freeJobs?.length > 0;
  const tableHeaders = ['maxSeeds', 'schedule', 'monthPrice'];

  return (
    <Box sx={{ overflow: 'hidden' }}>
      {projectSetupComplete(payment) ? (
        <section>
          <TableContainer sx={{ marginBottom: '1.5em' }}>
            <StyledTable>
              <TableHead>
                <TableRow>
                  {tableHeaders.map((header) => (
                    <TableCell key={header} size="small">
                      {t(`stepper.${header}`, {
                        ns: 'crawler',
                      })}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.values(CRAWLER_TYPES).map((option, i) => (
                  <TableRow key={i}>
                    <TableCell size="small">
                      {seedsDisplay(option.SEEDS)}
                    </TableCell>
                    <TableCell size="small">
                      {scheduleDisplay(option.SCHEDULE)}
                    </TableCell>
                    <TableCell size="small">
                      {priceDisplay(option.PRICE)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </StyledTable>
          </TableContainer>
          <Grid container spacing={2}>
            <Grid
              size={{
                xs: 12,
                sm: 6,
              }}
            >
              <Text sx={{ marginBottom: '1em' }}>
                {t('crawlerBillingNote', { ns: 'descriptions' })}
              </Text>
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 6,
              }}
            >
              <FormControl
                fullWidth
                size="small"
                variant="outlined"
                error={!!errors?.jobType}
              >
                <InputLabel>{t('crawlJobType', { ns: 'fields' })}</InputLabel>
                <Controller
                  name="jobType"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <Select
                      {...field}
                      onChange={(e) => {
                        setValue('seeds', '');
                        setValue('subseeds', '');
                        field.onChange(e);
                      }}
                      label={t('crawlJobType', { ns: 'fields' })}
                      inputProps={{ 'aria-label': 'job select' }}
                    >
                      {Object.entries(CRAWLER_TYPES).map((option) => (
                        <MenuItem
                          value={option[0]}
                          key={option[0]}
                          disabled={option[0] === JOB_TYPE.FREE && !hasFreeJobs}
                        >
                          {displayJobType(option[0])}
                          {option[0] === JOB_TYPE.FREE &&
                            watch('jobType') !== JOB_TYPE.FREE && (
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  bgcolor: 'text.disabled',
                                  borderRadius: '5px',
                                  ml: 1,
                                  px: 1,
                                  color: 'white',
                                }}
                              >
                                <Text caption>
                                  {t('remaining', {
                                    ns: 'general',
                                    number: freeJobs?.length || 0,
                                  })}
                                </Text>
                              </Box>
                            )}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
                <FormHelperText>{errors?.jobType?.message}</FormHelperText>
              </FormControl>

              <Text variant="body2" sx={{ mt: 2 }}>
                {t('crawlerPaymentNote', { ns: 'descriptions' })}
              </Text>
            </Grid>
          </Grid>
        </section>
      ) : (
        <ProjectSetupIncomplete
          serviceName={t('stepper.subscription', {
            ns: 'crawler',
          })}
          cards={payment.cards}
          billingInfo={payment.billingInfo}
          managers={payment.managers}
        />
      )}
    </Box>
  );
};

CrawlStep1.propTypes = {
  getFreeJobs: PropTypes.func.isRequired,
  payment: PropTypes.object,
};

export default CrawlStep1;
